import { NgFor } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { DocumentSelectOption } from '@gsm/entities';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { compareFiles } from '../../../helpers/compare-files.helper';
import { isAcceptedFiletype } from '../../../helpers/file-type.helper';
import { DEFAULT_ACCEPT_FILETYPES } from '../../shared-models/default-accept-filetypes.model';
import { UploadDokument } from '../../shared-models/upload-dokument.model';

export interface DragDropDokumentPickerChange {
  data: UploadDokument[];
  isValid: boolean;
}

@UntilDestroy()
@Component({
  selector:    'gsm-drag-drop-dokument-picker',
  templateUrl: './drag-drop-dokument-picker.component.html',
  styleUrls:   ['./drag-drop-dokument-picker.component.scss'],
  standalone:  true,
  imports:     [
    FormsModule,
    ReactiveFormsModule,
    NgFor,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    MatIconButton,
    MatIcon
  ]
})
export class DragDropDokumentPickerComponent implements OnInit,
                                                        OnDestroy {
  private fb = inject(UntypedFormBuilder);

  @Input() documentTypes: DocumentSelectOption[];
  @Input() acceptedFileTypes: string[] = DEFAULT_ACCEPT_FILETYPES;

  @Output() update = new EventEmitter<DragDropDokumentPickerChange>();

  files: File[] = [];
  isStyling = false;

  uploadForm: UntypedFormGroup;

  ngOnInit() {
    this.uploadForm = this.fb.group({
      fileInfo: this.fb.array([])
    });
    this.uploadForm.valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => this._emitChanges());
  }

  ngOnDestroy() {
    // needed for untilDestroyed to work
  }

  get acceptString(): string {
    return this.acceptedFileTypes ? this.acceptedFileTypes.join(',') : '';
  }

  /* Handling Drag and Drop */

  onDrop(ev: DragEvent) {
    ev.preventDefault();
    if (ev.dataTransfer.files.length > 0) {
      this._addFiles(Array.from(ev.dataTransfer.files));
      this.isStyling = false;
    }
  }

  onDragover(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    ev.dataTransfer.dropEffect = 'copy';
    this.isStyling = true;
  }

  onDragleave(ev: DragEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    this.isStyling = false;
  }

  /* Handling form array */

  get fileInfo(): UntypedFormArray {
    return this.uploadForm.get('fileInfo') as UntypedFormArray;
  }

  private _addFiles(files: File[]) {
    files.forEach((item: File) => {
      if (this._isUnique(item) && isAcceptedFiletype(item, this.acceptedFileTypes)) {
        this.files.push(item);
        // must be added after file, results in emit due to valueChanges handler
        this._addFileInfo();
      }
    });
  }

  private _newFileInfo(): UntypedFormGroup {
    return this.fb.group({
      type: [
        '',
        Validators.required
      ]
    });
  }

  private _addFileInfo() {
    this.fileInfo.push(this._newFileInfo());
  }

  private _removeFileInfo(i: number) {
    this.fileInfo.removeAt(i);
  }

  private _emitChanges() {
    this.update.emit({
      data:    this._getFinalOutput(),
      isValid: this.uploadForm.valid && this.files.length > 0
    });
  }

  /* General */

  manualUpload(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    this._addFiles(Array.from(fileInput.files));
    fileInput.value = '';
  }

  removeEntry(i: number) {
    this._removeFile(i);
    // must be removed after file, results in emit due to valueChanges handler
    this._removeFileInfo(i);
  }

  private _removeFile(index: number) {
    this.files.splice(index, 1);
  }

  /**
   * merges files and fileInfo into one array and returns it
   */
  private _getFinalOutput(): UploadDokument[] {
    return this.files.map((file, index) => {
      return {
        file,
        type: this.fileInfo.value[index].type
      };
    });
  }

  /**
   * returns true if the uploaded file is unique (so that the user does not upload the same file twice)
   */

  private _isUnique(file: File): boolean {
    return !this.files.some(f => compareFiles(f, file));
  }
}
